import React, { useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Scaffold } from "../Scaffold";
import LoadingSpinner from "../LoadingSpinner";
import { State } from "../redux/types";
import { useSelector, useDispatch } from "react-redux";
import { ConfirmActionDialog } from "../components/ConfirmActionDialog";
import { VendorUpdateDialog } from "../components/VendorUpdateDialog";
import { useVendorActions } from "../hooks/useVendorActions";
import { vendorRequest } from "../api/api";
import { setAdvanceResult } from "../redux/actions";
import {
  ZaxbysAdminCardShell,
  ZaxbysAdminCustomerCard,
  type AddressItem,
  type FavoriteStoreItem,
  type GiftCardItem,
} from "./ZaxbysAdminCustomerCard";
import { ZaxbysAdminModals } from "./ZaxbysAdminModals";

const VENDOR_KEY = "zaxbysadmin";

export const ZaxbysAdmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const advanceVendorResult = useSelector((state: State) => state.advanceResult);

  const [giftCardsModalOpen, setGiftCardsModalOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [favoriteStoresModalOpen, setFavoriteStoresModalOpen] = useState(false);

  const locationState = location.state as { customerVendorId?: string } | null;

  const zaxbyAccountId =
    advanceVendorResult?.customer?.zaxbyAccountId ??
    advanceVendorResult?.customer?.customerVendorId ??
    advanceVendorResult?.customer?.id ??
    advanceVendorResult?.value?.zaxbyAccountId ??
    advanceVendorResult?.value?.customerVendorId ??
    advanceVendorResult?.value?.id ??
    locationState?.customerVendorId ??
    "";

  const giftCardsList: GiftCardItem[] = advanceVendorResult?.customer?.giftCardsList || [];
  const addressesList: AddressItem[] = advanceVendorResult?.customer?.userAddressBooksList || [];
  const favoriteStoresList: FavoriteStoreItem[] =
    advanceVendorResult?.customer?.favoriteStoresList || [];

  const handleBackToResults = () => {
    navigate("/advanceresult");
  };

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleVendorActionSuccess = useCallback(
    async (actionKey: string) => {
      if (actionKey === "delete") {
        navigate("/advanceresult");
        return;
      }
      if (!zaxbyAccountId) {
        return;
      }
      setIsRefreshing(true);
      try {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 1000);
        });
        const response = await vendorRequest(zaxbyAccountId, VENDOR_KEY);
        if (response) {
          dispatch(setAdvanceResult({ ...response }));
        }
      } catch (error) {
        console.debug("Zaxbys vendor refetch failed", error);
      } finally {
        setIsRefreshing(false);
      }
    },
    [zaxbyAccountId, dispatch, navigate]
  );

  const {
    dialogOpen: vendorDialogOpen,
    updateDialogOpen: vendorUpdateDialogOpen,
    currentAction: vendorCurrentAction,
    loading: vendorLoading,
    openDialog: openVendorDialog,
    closeDialog: closeVendorDialog,
    closeUpdateDialog: closeVendorUpdateDialog,
    confirmAction: confirmVendorAction,
    confirmUpdate: confirmVendorUpdate,
  } = useVendorActions({
    vendorKey: VENDOR_KEY,
    customerVendorId: zaxbyAccountId,
    vendorName: "Zaxbys",
    onSuccess: handleVendorActionSuccess,
  });

  const buttonStyle = {
    backgroundColor: "#041E42",
    color: "white",
    px: 3,
    py: 1,
    textTransform: "none" as const,
    "&:hover": {
      backgroundColor: "#0a3d7a",
    },
  };

  if (!advanceVendorResult) {
    return (
      <Scaffold navBar center>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <LoadingSpinner />
        </Box>
      </Scaffold>
    );
  }

  return (
    <Scaffold navBar center>
      {isRefreshing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            bgcolor: "rgba(255,255,255,0.7)",
          }}
        >
          <LoadingSpinner />
        </Box>
      )}
      <Box
        sx={{
          position: "fixed",
          top: 80,
          left: 20,
          zIndex: 10,
          mt: "2rem",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToResults}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontSize: "0.875rem",
          }}
        >
          BACK TO RESULTS
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography
          variant="h4"
          sx={{ mb: 3, fontWeight: "bold", textAlign: "center", width: "100%" }}
        >
          Zaxby&apos;s Admin
        </Typography>

        <ZaxbysAdminCardShell>
          <ZaxbysAdminCustomerCard
            customer={advanceVendorResult?.customer}
            giftCardsList={giftCardsList}
            addressesList={addressesList}
            favoriteStoresList={favoriteStoresList}
            buttonStyle={buttonStyle}
            onOpenGiftCards={() => setGiftCardsModalOpen(true)}
            onOpenAddresses={() => setAddressModalOpen(true)}
            onOpenFavoriteStores={() => setFavoriteStoresModalOpen(true)}
            onUpdate={() => openVendorDialog("update")}
            onDelete={() => openVendorDialog("delete")}
          />
        </ZaxbysAdminCardShell>
      </Box>

      <ConfirmActionDialog
        open={vendorDialogOpen}
        actionLabel={vendorCurrentAction?.label ?? ""}
        description={vendorCurrentAction?.description ?? ""}
        confirmText={vendorCurrentAction?.confirmText}
        loading={vendorLoading}
        onConfirm={confirmVendorAction}
        onCancel={closeVendorDialog}
      />

      <VendorUpdateDialog
        open={vendorUpdateDialogOpen}
        vendorName="Zaxbys"
        loading={vendorLoading}
        initialValues={{
          firstName: advanceVendorResult?.customer?.firstName,
          lastName: advanceVendorResult?.customer?.lastName,
          email: advanceVendorResult?.customer?.email,
          phoneNumber: advanceVendorResult?.customer?.mobilePhoneNumber,
          dateOfBirth: advanceVendorResult?.customer?.birthDate,
        }}
        onConfirm={confirmVendorUpdate}
        onCancel={closeVendorUpdateDialog}
        hideBirthDate
      />

      <ZaxbysAdminModals
        giftCardsOpen={giftCardsModalOpen}
        onCloseGiftCards={() => setGiftCardsModalOpen(false)}
        giftCardsList={giftCardsList}
        addressOpen={addressModalOpen}
        onCloseAddresses={() => setAddressModalOpen(false)}
        addressesList={addressesList}
        favoriteStoresOpen={favoriteStoresModalOpen}
        onCloseFavoriteStores={() => setFavoriteStoresModalOpen(false)}
        favoriteStoresList={favoriteStoresList}
      />
    </Scaffold>
  );
};
